import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// functions

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { DataQuery, RucioLocationsType, Site, SiteStorageArea } from '../../utils/types';

// types specifically for this component.
import {
  QueryParameters,
  SelectedRowsType,
} from '../search-catalog/search-catalog-types';

export interface SearchCatalogCAOMState {
  //
  loadingAladin: boolean;

  // the retrieved data from the TAP service
  tapData: { id: string; tapData: any[] }[];
  loadingTapData: { id: string; loadingTapData: boolean }[];
  columns: { id: string; columns: string[] }[];

  //
  // the filter form state.
  //-------------------------------

  filter: {
    activeSearchTab: 'cone' | 'adql';
    sourceName: string;
    latestPositionSetBy: string;
    resolverText: string;
    currentlyResolving: boolean;
    sourceNameError: boolean;
    raValue: string;
    decValue: string;
    radiusValue: string;
    namespaceValue: string;
    filenameValue: string;
    collectionValue: string;
    facilityValue: string;
    instrumentValue: string;
    namespaceDisplayed: boolean;
    filenameDisplayed: boolean;
    collectionDisplayed: boolean;
    facilityDisplayed: boolean;
    instrumentDisplayed: boolean;
    positionSearch: boolean;
    dptValue: string | undefined;
    filterWindowSize: { width: number; height: number };
  };

  contextMenus: {
    refreshAddMetadata: number;
    refreshStageData: number;
  };
  aladinPosition: { ra: number; dec: number } | undefined;
  aladinFOV: number | undefined;
  filterMaximised: boolean;
  selectedQuery: DataQuery | undefined;
  existingQuery: {
    value: number;
    label: string;
  };
  queries: DataQuery[];
  adqlQuery: string;
  adqlError: string;

  errorMessage: { id: string; errorMessage: string }[];

  //
  // maintain a list of selected items.
  // --------------------------------------

  selectedItems: {
    //
    // the selected rows from the search-catalog table.
    selectedRows: SelectedRowsType[];

    // true if all items under a result tab are selected.
    selectAll: { id: string; selectAll: boolean }[];

    // a list of selected files on the selected-items tab which
    // have been selected again (i.e. ticked) by the user.
    selectedFiles: { namespace: string; filename: string }[];

    // should the gateway use the site with the best availability for
    // the selected files, or allow the user to choose?
    useBestSite: boolean;

    // the currently-selected site for the selected data items.
    selectedSite:
      | {
          value: Site;
          label: string;
          highlight: boolean;
        }
      | undefined;
    //
  };

  // maintain a list of selected footprints.
  selectedIDs: {
    id: string;
    ra: number;
    dec: number;
  }[];
  highlightedID: string;

  // details on the selected tab and search results.
  searchCatalogTab: string;
  searchResultsNextID: number;
  searchResults: QueryParameters[];
  tabDropdownDisplayed: boolean;

  // the aladin items.
  aladin: any | null;
  aladinMaximised: boolean;

  // the tab items.
  maxTabs: number;
  tabWidth: number;
  dropdownWidth: number;
  firstTab: number;

  // the list of ALL the data-product types.
  dataProductTypes: { dataproduct_type: string; num_records: number }[];

  // the list of data-product types found in each set of search results.
  dptInResults: { id: string; dpt: string[] }[];
  dptRetrieved: { id: string; dptRetrieved: boolean }[];

  // handle paging.
  pageSize: { id: string; pageSize: number }[]; // we have a page size for each results tab.
  total: { id: string; total: { dpt: string; total: number }[] }[]; // we have a total for each results tab, and for each data-product type.
  pages: { id: string; pages: number }[]; // we have a page count for each results tab.
  page: { id: string; page: number }[]; // we have a selected page for each results tab.
  dptFilter: { id: string; dptFilter: string }[]; // we have a data-product type filter for each results tab.
  displayedPage: {
    id: string;
    dptFilter: string;
    page: number;
    pageSize: number;
  }[];

  // the info panel on each result set.
  infoExpanded: { id: string; infoExpanded: boolean }[];
  builtQuery: { id: string; builtQuery: string | undefined }[];

  // selected-items panel state.
  selectedItemsPanelMaximised: boolean;

  // selected-items tab state.
  existingCollection: {
    value: number;
    label: string;
  };
  existingCollectionText: string;
  collections: {
    value: number;
    label: string;
  }[];
  dataWindowSize: {
    width: number;
    height: number;
  };
  //
} // SearchCatalogCAOMState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: SearchCatalogCAOMState = {
  tapData: [],
  loadingTapData: [],
  columns: [],
  filter: {
    activeSearchTab: 'cone',
    sourceName: '',
    latestPositionSetBy: 'movement',
    resolverText: 'Uses SIMBAD name resolver',
    currentlyResolving: false,
    sourceNameError: false,
    raValue: '',
    decValue: '',
    radiusValue: '',
    namespaceValue: '',
    filenameValue: '',
    collectionValue: '',
    facilityValue: '',
    instrumentValue: '',
    namespaceDisplayed: false,
    filenameDisplayed: false,
    collectionDisplayed: false,
    facilityDisplayed: false,
    instrumentDisplayed: false,
    positionSearch: true,
    dptValue: undefined,
    filterWindowSize: { width: 0.0, height: 0.0 },
  },
  contextMenus: {
    refreshAddMetadata: 0,
    refreshStageData: 0,
  },
  aladinPosition: undefined,
  aladinFOV: undefined,
  filterMaximised: true,
  selectedQuery: undefined,
  existingQuery: {
    value: -1,
    label: '',
  },
  queries: [],
  adqlQuery: 'SELECT TOP 10 * FROM ivoa.ObsCore',
  adqlError: '',
  errorMessage: [],
  selectedItems: {
    selectedRows: [],
    selectAll: [],
    selectedFiles: [],
    useBestSite: true,
    selectedSite: undefined,
  },
  selectedIDs: [],
  highlightedID: '',
  loadingAladin: true,
  searchCatalogTab: '',
  searchResultsNextID: 0,
  searchResults: [],
  tabDropdownDisplayed: false,
  aladin: null,
  aladinMaximised: true,
  maxTabs: -1,
  tabWidth: 131,
  dropdownWidth: 40,
  firstTab: 0,
  dataProductTypes: [],
  pageSize: [],
  total: [],
  pages: [],
  page: [],
  dptFilter: [],
  displayedPage: [],
  dptInResults: [],
  dptRetrieved: [],
  infoExpanded: [],
  builtQuery: [],
  selectedItemsPanelMaximised: true,
  existingCollection: { value: -1, label: '' },
  existingCollectionText: '',
  collections: [],
  dataWindowSize: {
    width: 0.0,
    height: 0.0,
  },
};

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const searchCatalogCAOMSlice = createSlice({
  name: 'searchCatalogCAOM',
  initialState: initialState,
  reducers: {
    //
    addSearchResults: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<QueryParameters>,
    ) => {
      //
      let newResult: QueryParameters = action.payload;
      newResult.id = (state.searchResultsNextID++).toString();

      // add array elements.
      state.searchResults = [...state.searchResults, newResult];
      state.total = [...state.total, { id: newResult.id, total: [] }];
      state.pages = [...state.pages, { id: newResult.id, pages: 0 }];
      state.page = [...state.page, { id: newResult.id, page: 0 }];
      state.pageSize = [...state.pageSize, { id: newResult.id, pageSize: 10 }];
      state.dptFilter = [...state.dptFilter, { id: newResult.id, dptFilter: '<all>' }];
      state.dptInResults = [...state.dptInResults, { id: newResult.id, dpt: [] }];
      state.dptRetrieved = [
        ...state.dptRetrieved,
        { id: newResult.id, dptRetrieved: false },
      ];
      state.infoExpanded = [
        ...state.infoExpanded,
        { id: newResult.id, infoExpanded: false },
      ];
      state.builtQuery = [
        ...state.builtQuery,
        { id: newResult.id, builtQuery: undefined },
      ];
      state.tapData = [...state.tapData, { id: newResult.id, tapData: [] }];
      state.loadingTapData = [
        ...state.loadingTapData,
        { id: newResult.id, loadingTapData: false },
      ];
      state.columns = [...state.columns, { id: newResult.id, columns: [] }];
      state.displayedPage = [
        ...state.displayedPage,
        { id: newResult.id, dptFilter: '', page: -1, pageSize: -1 },
      ];
      state.errorMessage = [
        ...state.errorMessage,
        { id: newResult.id, errorMessage: '' },
      ];
      state.selectedItems.selectAll = [
        ...state.selectedItems.selectAll,
        { id: newResult.id, selectAll: false },
      ];
      //
    },

    closeSearchResultsTab: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      //
      // switch tabs if we're closing the selected one.
      let newTabID: string = '';
      if (state.searchCatalogTab === action.payload) {
        //
        const page: { id: string; page: number }[] = Object.assign([], state.page);
        const index: number = page.findIndex(
          (item) => item.id === state.searchCatalogTab,
        );

        // try to select the tab that follows.
        if (index < page.length - 1) {
          newTabID = page[index + 1].id;
        } else {
          //
          // if that fails, try to select the previous tab.
          if (index > 0) {
            newTabID = page[index - 1].id;
          }
          //
        }
      }
      state.searchCatalogTab = newTabID;

      // remove array elements.
      state.searchResults = state.searchResults.filter(
        (item) => item.id !== action.payload,
      );
      state.total = state.total.filter((item) => item.id !== action.payload);
      state.pages = state.pages.filter((item) => item.id !== action.payload);
      state.page = state.page.filter((item) => item.id !== action.payload);
      state.pageSize = state.pageSize.filter((item) => item.id !== action.payload);
      state.dptFilter = state.dptFilter.filter((item) => item.id !== action.payload);
      state.dptInResults = state.dptInResults.filter(
        (item) => item.id !== action.payload,
      );
      state.dptRetrieved = state.dptRetrieved.filter(
        (item) => item.id !== action.payload,
      );
      state.infoExpanded = state.infoExpanded.filter(
        (item) => item.id !== action.payload,
      );
      state.builtQuery = state.builtQuery.filter((item) => item.id !== action.payload);
      state.tapData = state.tapData.filter((item) => item.id !== action.payload);
      state.loadingTapData = state.loadingTapData.filter(
        (item) => item.id !== action.payload,
      );
      state.columns = state.columns.filter((item) => item.id !== action.payload);
      state.displayedPage = state.displayedPage.filter(
        (item) => item.id !== action.payload,
      );
      state.errorMessage = state.errorMessage.filter(
        (item) => item.id !== action.payload,
      );
      state.selectedItems.selectAll = state.selectedItems.selectAll.filter(
        (item) => item.id !== action.payload,
      );
      //
    },

    rowCheckedHandler: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ rows: SelectedRowsType[]; ticked: boolean[] }>,
    ) => {
      //
      // first, check that we've got the same number of rows and tick flags.
      if (action.payload.rows.length === action.payload.ticked.length) {
        //
        state.selectedItems.selectedRows = [
          //
          // items already exist in the state array, and have been unticked, so they should be removed.
          ...state.selectedItems.selectedRows.filter((item) => {
            const index: number = action.payload.rows.findIndex(
              (find) => find.obsPublisherDid === item.obsPublisherDid,
            );
            if (index > -1) {
              return action.payload.ticked[index];
            } else {
              return true;
            }
          }),
          ...action.payload.rows.filter((item, index) => {
            //
            // items don't exist in the state array, but have been ticked, so they should be added.
            if (
              state.selectedItems.selectedRows.findIndex(
                (find) => find.obsPublisherDid === item.obsPublisherDid,
              ) === -1 &&
              action.payload.ticked[index] === true
            ) {
              return true;
            } else {
              return false;
            }
            //
          }),
        ];
        //
      }
      //
    },

    setActiveSearchTab: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<'cone' | 'adql'>,
    ) => {
      state.filter.activeSearchTab = action.payload;
    },

    setADQLError: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.adqlError = action.payload;
    },

    setADQLQuery: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.adqlQuery = action.payload;
    },

    setAladin: (state: SearchCatalogCAOMState, action: PayloadAction<any | null>) => {
      state.aladin = action.payload;
    },

    setAladinFOV: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<number | undefined>,
    ) => {
      state.aladinFOV = action.payload;
    },

    setAladinMaximised: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.aladinMaximised = action.payload;
    },

    setAladinPosition: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ ra: number; dec: number } | undefined>,
    ) => {
      state.aladinPosition = action.payload;
    },

    setBuiltQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; builtQuery: string | undefined }>,
    ) => {
      //
      state.builtQuery = state.builtQuery.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setCollectionDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.collectionDisplayed = action.payload;
    },

    setCollectionValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.filter.collectionValue = action.payload;
    },

    setCollections: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<
        {
          value: number;
          label: string;
        }[]
      >,
    ) => {
      state.collections = action.payload;
    },

    setColumns: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; columns: string[] }>,
    ) => {
      //
      state.columns = state.columns.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setContextMenuRefreshAddMetadata: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<number>,
    ) => {
      state.contextMenus.refreshAddMetadata = action.payload;
    },

    setContextMenuRefreshStageData: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<number>,
    ) => {
      state.contextMenus.refreshStageData = action.payload;
    },

    setCurrentlyResolving: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.currentlyResolving = action.payload;
    },

    setDataProductTypes: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ dataproduct_type: string; num_records: number }[]>,
    ) => {
      state.dataProductTypes = action.payload;
    },

    setDataWindowSize: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.dataWindowSize = action.payload;
    },

    setDecValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.decValue = action.payload;
    },

    setDisplayedPage: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{
        id: string;
        dptFilter: string;
        page: number;
        pageSize: number;
      }>,
    ) => {
      //
      state.displayedPage = state.displayedPage.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setDPTFilter: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; dptFilter: string }>,
    ) => {
      //
      state.dptFilter = state.dptFilter.map((item) => {
        //
        // update the data-product type filter for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });

      // re-calculate the pages, and set the page.
      state.pages = state.pages.map((item) => {
        //
        if (item.id === action.payload.id) {
          //
          const totalAllDPTs: { dpt: string; total: number }[] | undefined =
            state.total.find((find) => find.id === item.id)?.total ?? undefined;
          const total: number | undefined =
            totalAllDPTs?.find((find) => find.dpt === action.payload.dptFilter)?.total ??
            undefined;
          const pageSize: number | undefined =
            state.pageSize.find((find) => find.id === item.id)?.pageSize ?? 10;

          return { id: item.id, pages: Math.ceil((total ?? 0) / pageSize) };
          //
        } else {
          return item;
        }
        //
      });
      state.page = state.page.map((item) => {
        //
        if (item.id === action.payload.id) {
          //
          const pages: number | undefined =
            state.pages.find((find) => find.id === item.id)?.pages ?? undefined;

          return {
            id: item.id,
            page: item.page >= (pages ?? 1) ? (pages ?? 1) - 1 : item.page,
          };
          //
        } else {
          return item;
        }
        //
      });
      //
    },

    setDPTInResults: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; dpt: string[] }>,
    ) => {
      //
      state.dptInResults = state.dptInResults.map((item) => {
        //
        // update the data-product types found for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
    },

    setDPTRetrieved: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; dptRetrieved: boolean }>,
    ) => {
      //
      state.dptRetrieved = state.dptRetrieved.map((item) => {
        //
        // update the data-product types found for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
    },

    setDPTValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.filter.dptValue = action.payload;
    },

    setDropdownWidth: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.dropdownWidth = action.payload;
    },

    setErrorMessage: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; errorMessage: string }>,
    ) => {
      //
      state.errorMessage = state.errorMessage.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setExistingCollection: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ value: number; label: string }>,
    ) => {
      state.existingCollection = action.payload;
    },

    setExistingCollectionText: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.existingCollectionText = action.payload;
    },

    setExistingQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ value: number; label: string }>,
    ) => {
      state.existingQuery = action.payload;
    },

    setFacilityDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.facilityDisplayed = action.payload;
    },

    setFacilityValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.facilityValue = action.payload;
    },

    setFilenameDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.filenameDisplayed = action.payload;
    },

    setFilenameValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.filenameValue = action.payload;
    },

    setFilterMaximised: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filterMaximised = action.payload;
    },

    setFilterWindowSize: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.filter.filterWindowSize = action.payload;
    },

    setFirstTab: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.firstTab = action.payload;
    },

    setHighlightedID: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.highlightedID = action.payload;
    },

    setInfoExpanded: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; infoExpanded: boolean }>,
    ) => {
      //
      state.infoExpanded = state.infoExpanded.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setInstrumentDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.instrumentDisplayed = action.payload;
    },

    setInstrumentValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.filter.instrumentValue = action.payload;
    },

    setLatestPositionSetBy: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.filter.latestPositionSetBy = action.payload;
    },

    setLoadingAladin: (state: SearchCatalogCAOMState, action: PayloadAction<boolean>) => {
      state.loadingAladin = action.payload;
    },

    setLoadingTapData: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; loadingTapData: boolean }>,
    ) => {
      //
      state.loadingTapData = state.loadingTapData.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setMaxTabs: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.maxTabs = action.payload;
    },

    setNamespaceDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.namespaceDisplayed = action.payload;
    },

    setNamespaceValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.namespaceValue = action.payload;
    },

    setPage: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; page: number }>,
    ) => {
      //
      state.page = state.page.map((item) => {
        //
        // update the pages for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setPages: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; pages: number }>,
    ) => {
      //
      state.pages = state.pages.map((item) => {
        //
        // update the pages for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setPageSize: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; pageSize: number }>,
    ) => {
      //
      const oldPageSize: number =
        state.pageSize.find((item) => item.id === action.payload.id)?.pageSize ?? -1;

      // set new page size.
      state.pageSize = state.pageSize.map((item) => {
        //
        // update the page size for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });

      // re-calculate the pages, and set the page.
      state.pages = state.pages.map((item) => {
        //
        if (item.id === action.payload.id) {
          //
          const dptFilter: string =
            state.dptFilter.find((find) => find.id === item.id)?.dptFilter ?? '<all>';
          const totalAllDPTs: { dpt: string; total: number }[] | undefined =
            state.total.find((find) => find.id === item.id)?.total ?? undefined;
          const total: number | undefined =
            totalAllDPTs?.find((find) => find.dpt === dptFilter)?.total ?? undefined;

          return {
            id: item.id,
            pages: Math.ceil((total ?? 0) / action.payload.pageSize),
          };
          //
        } else {
          return item;
        }
        //
      });
      state.page = state.page.map((item) => {
        //
        if (item.id === action.payload.id) {
          //
          // calculate the row indexes of the first row.
          const firstRow: number = oldPageSize * item.page;

          // calculate the new page numbers, and update the state.
          const currentPage: number = Math.floor(firstRow / action.payload.pageSize);

          return { id: item.id, page: currentPage };
          //
        } else {
          return item;
        }
        //
      });
      //
    },

    setPositionSearch: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.positionSearch = action.payload;
    },

    setQueries: (state: SearchCatalogCAOMState, action: PayloadAction<DataQuery[]>) => {
      state.queries = action.payload;
    },

    setRadiusValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.radiusValue = action.payload;
    },

    setRAValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.raValue = action.payload;
    },

    setResolverText: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.resolverText = action.payload;
    },

    setSearchCatalogTab: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string | null>,
    ) => {
      //
      const newTab: string =
        action.payload ??
        (state.searchResults.length > 0
          ? state.searchResults[state.searchResults.length - 1].id
          : '');

      // if we're switching tabs then hide the expanded info panel.
      if (state.searchCatalogTab !== newTab) {
        setInfoExpanded({ id: state.searchCatalogTab, infoExpanded: false });
      }

      state.searchCatalogTab = newTab;
      //
    },

    setSearchResults: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<QueryParameters[]>,
    ) => {
      state.searchResults = action.payload;
    },

    setSelectAll: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; selectAll: boolean }>,
    ) => {
      //
      state.selectedItems.selectAll = state.selectedItems.selectAll.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setSelectedFiles: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ namespace: string; filename: string }[]>,
    ) => {
      state.selectedItems.selectedFiles = action.payload;
    },

    setSelectedIDs: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<
        {
          id: string;
          ra: number;
          dec: number;
        }[]
      >,
    ) => {
      state.selectedIDs = action.payload;
    },

    setSelectedItemsPanelMaximised: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.selectedItemsPanelMaximised = action.payload;
    },

    setSelectedQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<DataQuery | undefined>,
    ) => {
      state.selectedQuery = action.payload;
    },

    setSelectedRows: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<SelectedRowsType[]>,
    ) => {
      state.selectedItems.selectedRows = action.payload;
    },

    setSelectedSite: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<
        | {
            value: Site;
            label: string;
            highlight: boolean;
          }
        | undefined
      >,
    ) => {
      state.selectedItems.selectedSite = action.payload;
    },

    setSourceName: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.sourceName = action.payload;
    },

    setSourceNameError: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.sourceNameError = action.payload;
    },

    setTabDropdownDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.tabDropdownDisplayed = action.payload;
    },

    setTabWidth: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.tabWidth = action.payload;
    },

    setTapData: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; tapData: any[] }>,
    ) => {
      //
      state.tapData = state.tapData.map((item) => {
        //
        // update the state for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setTotal: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ id: string; total: { dpt: string; total: number }[] }>,
    ) => {
      //
      state.total = state.total.map((item) => {
        //
        // update the total for a specific results tab.
        if (item.id === action.payload.id) {
          return action.payload;
        } else {
          return item;
        }
        //
      });
      //
    },

    setUseBestSite: (state: SearchCatalogCAOMState, action: PayloadAction<boolean>) => {
      state.selectedItems.useBestSite = action.payload;
    },

    updateSelectedFiles: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{
        addFiles?: {
          namespace: string;
          filename: string;
        }[];
        removeFiles?: {
          namespace: string;
          filename: string;
        }[];
      }>,
    ) => {
      //
      state.selectedItems.selectedFiles = [
        //
        // remove items.
        ...state.selectedItems.selectedFiles.filter(
          (item) =>
            (action.payload.removeFiles?.findIndex(
              (find) =>
                find.namespace === item.namespace && find.filename === item.filename,
            ) ?? -1) === -1,
        ),

        // add items.
        ...(action.payload.addFiles?.filter(
          (item) =>
            state.selectedItems.selectedFiles.findIndex(
              (find) =>
                find.namespace === item.namespace && find.filename === item.filename,
            ) === -1,
        ) ?? []),
      ];
      //
    },
    //
  },
  //extraReducers: (builder) => {
  //  builder
  //    .addCase(initiateDMSearch.fulfilled, (state: FileBrowserState) => {})
  //},
});

// expose the interface.
export const {
  addSearchResults,
  closeSearchResultsTab,
  rowCheckedHandler,
  setActiveSearchTab,
  setADQLError,
  setADQLQuery,
  setAladin,
  setAladinFOV,
  setAladinMaximised,
  setAladinPosition,
  setBuiltQuery,
  setCollectionDisplayed,
  setCollectionValue,
  setCollections,
  setColumns,
  setContextMenuRefreshAddMetadata,
  setContextMenuRefreshStageData,
  setCurrentlyResolving,
  setDataProductTypes,
  setDataWindowSize,
  setDecValue,
  setDisplayedPage,
  setDPTFilter,
  setDPTInResults,
  setDPTRetrieved,
  setDPTValue,
  setDropdownWidth,
  setErrorMessage,
  setExistingCollection,
  setExistingCollectionText,
  setExistingQuery,
  setFacilityDisplayed,
  setFacilityValue,
  setFilenameDisplayed,
  setFilenameValue,
  setFilterMaximised,
  setFilterWindowSize,
  setFirstTab,
  setHighlightedID,
  setInfoExpanded,
  setInstrumentDisplayed,
  setInstrumentValue,
  setLatestPositionSetBy,
  setLoadingAladin,
  setLoadingTapData,
  setMaxTabs,
  setNamespaceDisplayed,
  setNamespaceValue,
  setPage,
  setPages,
  setPageSize,
  setPositionSearch,
  setQueries,
  setRAValue,
  setRadiusValue,
  setResolverText,
  setSearchCatalogTab,
  setSearchResults,
  setSelectAll,
  setSelectedFiles,
  setSelectedIDs,
  setSelectedItemsPanelMaximised,
  setSelectedQuery,
  setSelectedRows,
  setSelectedSite,
  setSourceName,
  setSourceNameError,
  setTabDropdownDisplayed,
  setTabWidth,
  setTapData,
  setTotal,
  setUseBestSite,
  updateSelectedFiles,
} = searchCatalogCAOMSlice.actions;
export default searchCatalogCAOMSlice.reducer;
